import config from '../config';

class ApiService {
  constructor() {
    this.apiUrl = config.apiEndpoint;
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  async request(endpoint, options = {}) {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      ...options.headers
    };

    const startTime = performance.now();

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      // Handle 401 Unauthorized - session expired
      if (response.status === 401) {
        console.warn('🚨 Session expired (401), redirecting to login...');
        // Clear any stored tokens
        localStorage.clear();
        sessionStorage.clear();
        // Redirect to login page
        window.location.href = '/';
        throw new Error('Session expired. Please log in again.');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      console.error(`${options.method || 'GET'} ${endpoint} failed after ${duration}ms:`, error);
      throw error;
    }
  }

  async bulkImportPatients(csvData) {
    return this.request('/patients/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ csvData })
    });
  }

  async getUsers() {
    return this.request('/admin/users');
  }

  async createUser(data) {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteUser(username) {
    return this.request(`/admin/users/${username}`, {
      method: 'DELETE'
    });
  }

  async updateUser(username, data) {
    return this.request(`/admin/users/${username}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getCurrentUser() {
    // This would call a Lambda endpoint that returns current user info from JWT
    return this.request('/auth/me');
  }

  // ============================================
  // PROTOCOL OVERVIEW (dashboard)
  // ============================================
  async getProtocolStats() {
    return this.request('/dashboard/protocol-stats');
  }
  async getProtocolsByCategory(category) {
    return this.request(`/dashboard/protocols?category=${encodeURIComponent(category)}`);
  }
  // ============================================
  // PATIENTS
  // ============================================
  async getPatients(queryString = '') {
    return this.request(`/patients${queryString}`);
  }

  async getPatient(patientId) {
    return this.request(`/patients/${patientId}`);
  }

  async createPatient(data) {
    return this.request('/patients', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePatient(patientId, data) {
    return this.request(`/patients/${patientId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePatient(patientId) {
    return this.request(`/patients/${patientId}`, {
      method: 'DELETE'
    });
  }

  async touchPatient(patientId) {
    return this.request(`/patients/${patientId}/touch`, {
      method: 'POST'
    });
  }

  async getPatientNotes(patientId) {
    return this.request(`/patients/${patientId}/notes`);
  }

  async getPatientNote(patientId, noteId) {
    return this.request(`/patients/${patientId}/notes/${noteId}`);
  }

  async createPatientNote(patientId, data) {
    return this.request(`/patients/${patientId}/notes`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePatientNote(patientId, noteId, data) {
    return this.request(`/patients/${patientId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePatientNote(patientId, noteId) {
    return this.request(`/patients/${patientId}/notes/${noteId}`, {
      method: 'DELETE'
    });
  }
  // Get recently accessed patients for dashboard
  async getRecentlyAccessedPatients() {
    return this.request('/dashboard/recent-patients');
  }
  // ============================================
  // MEDICATIONS (Master List)
  // ============================================
  async getMedications() {
    return this.request('/medications');
  }

  async getMedication(medicationId) {
    return this.request(`/medications/${medicationId}`);
  }

  async createMedication(data) {
    return this.request('/medications', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateMedication(medicationId, data) {
    return this.request(`/medications/${medicationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteMedication(medicationId) {
    return this.request(`/medications/${medicationId}`, {
      method: 'DELETE'
    });
  }

  async checkMedicationAssignments(medicationId) {
    return this.request(`/medications/${medicationId}/assignments`);
  }

  // ============================================
  // PATIENT MEDICATIONS (Assignments)
  // ============================================
  async getPatientMedications(patientId) {
    return this.request(`/patients/${patientId}/medications`);
  }

  async addMedicationToPatient(patientId, data) {
    return this.request(`/patients/${patientId}/medications`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePatientMedication(patientId, patientMedicationId, data) {
    return this.request(`/patients/${patientId}/medications/${patientMedicationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePatientMedication(patientId, patientMedicationId) {
    return this.request(`/patients/${patientId}/medications/${patientMedicationId}`, {
      method: 'DELETE'
    });
  }

  // ============================================
  // PROTOCOLS (Master Templates)
  // ============================================
  async getProtocols() {
    return this.request('/protocols');
  }

  async getProtocol(protocolId) {
    return this.request(`/protocols/${protocolId}`);
  }

  async createProtocol(data) {
    return this.request('/protocols', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateProtocol(protocolId, data) {
    return this.request(`/protocols/${protocolId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteProtocol(protocolId) {
    return this.request(`/protocols/${protocolId}`, {
      method: 'DELETE'
    });
  }


  // ============================================
  // PATIENT PROTOCOLS (Assignments)
  // ============================================
  async getPatientProtocols(patientId) {
    return this.request(`/patients/${patientId}/protocols`);
  }

  async createPatientProtocol(patientId, data) {
    return this.request(`/patients/${patientId}/protocols`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePatientProtocol(patientId, protocolId, data) {
    return this.request(`/patients/${patientId}/protocols/${protocolId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePatientProtocol(patientId, protocolId) {
    return this.request(`/patients/${patientId}/protocols/${protocolId}`, {
      method: 'DELETE'
    });
  }

  // ============================================
  // SERVICES (Master List)
  // ============================================
  async getServices() {
    return this.request('/services');
  }

  async getService(serviceId) {
    return this.request(`/services/${serviceId}`);
  }

  async createService(data) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateService(serviceId, data) {
    return this.request(`/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteService(serviceId) {
    return this.request(`/services/${serviceId}`, {
      method: 'DELETE'
    });
  }

  async checkServiceAssignments(serviceId) {
    return this.request(`/services/${serviceId}/assignments`);
  }

  // ============================================
  // PATIENT SERVICES (Scheduled Services)
  // ============================================
  async getPatientServices(patientId) {
    return this.request(`/patients/${patientId}/services`);
  }

  async addServiceToPatient(patientId, data) {
    return this.request(`/patients/${patientId}/services`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePatientService(patientId, patientServiceId, data) {
    return this.request(`/patients/${patientId}/services/${patientServiceId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePatientService(patientId, patientServiceId) {
    return this.request(`/patients/${patientId}/services/${patientServiceId}`, {
      method: 'DELETE'
    });
  }

  // ============================================
  // DASHBOARD
  // ============================================
  async getUpcomingServices() {
    return this.request('/services/upcoming');
  }

  async getRemindersToday() {
    return this.request('/reminders/today');
  }

  // ==================== DOCUMENT METHODS ====================

  async getPatientDocuments(patientId) {
    return this.request(`/patients/${patientId}/documents`);
  }

  async getDocumentUploadUrl(patientId, filename, contentType) {
    return this.request('/documents/upload-url', {
      method: 'POST',
      body: JSON.stringify({ patientId, filename, contentType })
    });
  }

  async confirmDocumentUpload(documentId, fileSize) {
    return this.request(`/documents/${documentId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ fileSize })
    });
  }

  async getDocumentViewUrl(documentId) {
    return this.request(`/documents/${documentId}/view`);
  }

  async deleteDocument(documentId) {
    return this.request(`/documents/${documentId}`, {
      method: 'DELETE'
    });
  }

  // Upload file to S3 using pre-signed URL
  async uploadFileToS3(presignedUrl, file) {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }

    return response;
  }

  // ============================================
  // RBAC - Permissions
  // ============================================
  async getPermissions() {
    return this.request('/rbac/permissions');
  }

  // ============================================
  // RBAC - Roles
  // ============================================
  async getRoles() {
    return this.request('/rbac/roles');
  }

  async getRole(roleId) {
    return this.request(`/rbac/roles/${roleId}`);
  }

  async createRole(roleData) {
    return this.request('/rbac/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  }

  async updateRole(roleId, roleData) {
    return this.request(`/rbac/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    });
  }

  async deleteRole(roleId) {
    return this.request(`/rbac/roles/${roleId}`, {
      method: 'DELETE'
    });
  }

  // ============================================
  // RBAC - User Roles
  // ============================================
  async getUserRoles(username) {
    return this.request(`/rbac/users/${username}/roles`);
  }

  async assignRoleToUser(username, roleId) {
    return this.request(`/rbac/users/${username}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleId })
    });
  }

  async removeRoleFromUser(username, roleId) {
    return this.request(`/rbac/users/${username}/roles/${roleId}`, {
      method: 'DELETE'
    });
  }

  async getUserPermissions(username) {
    return this.request(`/rbac/users/${username}/permissions`);
  }

  // Add these methods to your ApiService.js

  // Add these methods to your ApiService.js

  // Get patient statistics (total, new this month, etc.)
  async getPatientStats() {
    try {
      const response = await this.request('/patients/stats', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Failed to get patient stats:', error);
      throw error;
    }
  }

  // Get list of patients without any assigned protocols
  async getPatientsWithoutProtocols() {
    try {
      const response = await this.request('/patients/no-protocols', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Failed to get patients without protocols:', error);
      throw error;
    }
  }

  // Get latest duplicate scan results (already created earlier)
  async getLatestDuplicateScan() {
    try {
      const response = await this.request('/admin/duplicates/latest', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Failed to get duplicate scan:', error);
      // Return empty result instead of throwing
      return {
        summary: {
          duplicateGroups: 0,
          totalDuplicates: 0,
          scanDate: null
        },
        groups: []
      };
    }
  }

  // Trigger manual duplicate scan (already created earlier)
  async triggerDuplicateScan() {
    try {
      const response = await this.request('/admin/duplicates/scan', {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Failed to trigger duplicate scan:', error);
      throw error;
    }
  }

  // ==================== DUPLICATE RESOLUTION ====================

  async restorePatient(patientId) {
    return this.request(`/patients/${patientId}/restore`, { method: 'POST' });
  }

  async getDeletedPatients() {
    return this.request('/admin/deleted-patients');
  }

  async purgePatient(patientId) {
    return this.request(`/admin/deleted-patients/${patientId}/purge`, { method: 'DELETE' });
  }

  async getPatientAudit(patientId) {
    return this.request(`/patients/${patientId}/audit`);
  }

  async getActivityLog(days = 7, limit = 200) {
    return this.request(`/admin/audit?days=${days}&limit=${limit}`);
  }

  async mergeDuplicates(groupId, primaryPatientId) {
    try {
      const response = await fetch(`${this.apiUrl}/admin/duplicates/${groupId}/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify({ primaryPatientId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to merge duplicates');
      }

      return await response.json();
    } catch (error) {
      console.error('Error merging duplicates:', error);
      throw error;
    }
  }

  async markAsNotDuplicate(groupId) {
    try {
      const response = await fetch(`${this.apiUrl}/admin/duplicates/${groupId}/not-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to mark as not duplicate');
      }

      return await response.json();
    } catch (error) {
      console.error('Error marking as not duplicate:', error);
      throw error;
    }
  }

  async getPatientMedications(patientId) {
    try {
      const response = await fetch(`${this.apiUrl}/patients/${patientId}/medications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient medications');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching patient medications:', error);
      return [];
    }
  }

  async getPatientProtocols(patientId) {
    try {
      const response = await fetch(`${this.apiUrl}/patients/${patientId}/protocols`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient protocols');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching patient protocols:', error);
      return [];
    }
  }
}


const apiService = new ApiService();
export default apiService;